import { useMemo } from 'react';
import { useTeamStore } from '../../../stores/teamStore';
import { usePilgrimGuideStore } from '../../../stores/pilgrimGuideStore';
import { usePackagesStore } from '../../../stores/packagesStore';
import { useFlightsStore } from '../../../stores/flightsStore';
import { useAccommodationStore } from '../../../stores/accommodationStore';
import { useDisputesStore } from '../../../stores/disputesStore';
import { useGuidesStore } from '../../../stores/guidesStore';
import { usePilgrimsStore } from '../../../stores/pilgrimsStore';
import { computeOrgStats } from '../../../lib/orgStats';
import { calculateOccupancy, calculatePackageCompletion } from '../utils/calculations';

const useAdminStats = () => {
  const { users, loading: usersLoading } = useTeamStore();
  const { packages, loading: packagesLoading } = usePackagesStore();
  const { flights, loading: flightsLoading } = useFlightsStore();
  const { tables, loading: tablesLoading } = useAccommodationStore();
  const { disputes, loading: disputesLoading } = useDisputesStore();
  const { guides, loading: guidesLoading } = useGuidesStore();

  const isLoading = usersLoading || packagesLoading || flightsLoading || 
                    tablesLoading || disputesLoading || guidesLoading;

  const stats = useMemo(() => {
    const pilgrims = Object.values(guides).reduce((total, guide) => {
      const guidePilgrims = Object.values(guide.pilgrims || {}).length;
      return total + guidePilgrims;
    }, 0);

    const occupancyRate = calculateOccupancy(tables);
    const packageCompletionRate = calculatePackageCompletion(packages);
    const activeDisputes = Object.values(disputes).filter(d => d.status === 'pending').length;

    return {
      // Main Stats
      pilgrims,
      packages: Object.keys(packages).length,
      flights: Object.keys(flights).length,
      hotels: Object.keys(tables).length,

      // Quick Stats
      occupancy: occupancyRate,
      packageCompletion: packageCompletionRate,
      disputes: activeDisputes,

      // Additional Stats
      totalGuides: Object.keys(guides).length,
      totalUsers: Object.keys(users).length,
      activeUsers: Object.values(users).filter(u => u.status === 'active').length
    };
  }, [users, packages, flights, tables, disputes, guides]);

  return { stats, isLoading };
};

const useOrganizerStats = (organizerId: string) => {
  const { guides, loading: guidesLoading } = useGuidesStore();
  const { links, loading: linksLoading } = usePilgrimGuideStore();
  const { disputes, loading: disputesLoading } = useDisputesStore();
  const { tables, loading: tablesLoading } = useAccommodationStore();
  const { pilgrims } = usePilgrimsStore();

  const loading = guidesLoading || linksLoading || disputesLoading || tablesLoading;

  const stats = useMemo(() => {
    const organizerGuides = Object.values(guides).filter(g => g.organizerId === organizerId);

    // Single source of truth for org-level pilgrim/dispute counts.
    const s = computeOrgStats(organizerId, links, pilgrims, disputes);

    // Pilgrims linked to ANY of this org's links (organizerId-keyed, includes
    // waiting_* placeholders that don't have a guideId).
    const organizerPilgrims = Object.values(links).filter(
      link => link.organizerId === organizerId
    );

    const accommodatedPilgrims = organizerPilgrims.filter(p =>
      Object.values(tables).some(table =>
        table.rooms.some(room =>
          room.beds.some(bed => bed.pilgrimId === p.pilgrimId)
        )
      )
    ).length;

    return {
      totalGuides: organizerGuides.length,
      totalPilgrims: s.inBooking,         // distinct passports in booking
      accommodatedPilgrims,
      notOwner: s.notOwnerCount,           // renamed from misleading "pendingLinks"
      pendingOnly: s.pendingOnly,          // distinct passports pending-only
      disputes: s.disputed,                // active disputes naming this org
      recentPilgrims: organizerPilgrims
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 5),
    };
  }, [guides, links, pilgrims, disputes, tables, organizerId]);

  return { stats, loading };
};

export const useGuideStats = (guideId: string) => {
  const { links, loading: linksLoading } = usePilgrimGuideStore();
  const { disputes, loading: disputesLoading } = useDisputesStore();
  const { tables, loading: tablesLoading } = useAccommodationStore();

  const loading = linksLoading || disputesLoading || tablesLoading;

  const stats = useMemo(() => {
    // Get pilgrims for this guide
    const guidePilgrims = Object.values(links).filter(
      link => link.guideId === guideId
    );

    // Calculate stats
    const total = guidePilgrims.length;
    const notOwnerCount = guidePilgrims.filter(p => p.isOwner === false).length;
    const disputed = guidePilgrims.filter(p => p.status === 'disputed').length;

    // Calculate accommodation stats
    const accommodated = guidePilgrims.filter(p => {
      // Check if pilgrim is accommodated in any table
      return Object.values(tables).some(table =>
        table.rooms.some(room =>
          room.beds.some(bed => bed.pilgrimId === p.pilgrimId)
        )
      );
    }).length;

    return {
      total,
      accommodated,
      pending: notOwnerCount,
      disputed,
      recentPilgrims: guidePilgrims
        .sort((a, b) => b.createdAt - a.createdAt)
        .slice(0, 5)
    };
  }, [links, disputes, tables, guideId]);

  return { stats, loading };
};
